import type React from "react";
import { fmtInt } from "@/lib/format";
import type { Slide, SlideContext } from "../slide.types";
import { Tile } from "../visuals/Tile";
import { Card, CardLabel } from "../visuals/Card";
import { DonutWithLegend, type DonutDatum } from "../visuals/DonutWithLegend";
import { TopBars, type TopBarItem } from "../visuals/TopBars";
import { ACCENT_GREEN, ACCENT_BLUE, ACCENT_RED, ACCENT_ORANGE } from "../colors";

function AppUsageSlideView({ appUserData, resetKey }: SlideContext): React.JSX.Element {
  if (appUserData === null || appUserData.total_users === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-white/40 text-sm font-body">
          No app user roster uploaded for this client.
        </p>
      </div>
    );
  }

  const { total_users, active_users, invited_users, expired_users, adoption_rate, by_team } =
    appUserData;

  const statusData: DonutDatum[] = [
    { name: "Active", value: active_users, color: ACCENT_GREEN },
    { name: "Invited", value: invited_users, color: ACCENT_BLUE },
    { name: "Expired", value: expired_users, color: ACCENT_RED },
  ].filter((d) => d.value > 0);

  const topTeams: TopBarItem[] = [...by_team]
    .sort((a, b) => b.total - a.total)
    .slice(0, 5)
    .map((t) => ({
      label: t.team,
      value: t.total,
      display: fmtInt(t.total),
    }));

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <Tile
          label="Registered Users"
          value={total_users}
          format={(v) => fmtInt(Math.round(v))}
          delay={100}
          resetKey={resetKey}
        />
        <Tile
          label="App Adoption Rate"
          value={adoption_rate ?? 0}
          format={(v) => (adoption_rate === null ? "—" : `${v.toFixed(1)}%`)}
          delay={200}
          accent={ACCENT_ORANGE}
          sub="check-in, emergency, or team-alert activity ÷ registered"
          resetKey={resetKey}
        />
        <Tile
          label="Invited (Pending)"
          value={invited_users}
          format={(v) => fmtInt(Math.round(v))}
          delay={300}
          resetKey={resetKey}
        />
        <Tile
          label="Expired Invitations"
          value={expired_users}
          format={(v) => fmtInt(Math.round(v))}
          delay={400}
          resetKey={resetKey}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {statusData.length > 0 ? (
          <DonutWithLegend
            title="Registration Status"
            data={statusData}
            centerValue={total_users}
            centerLabel="users"
            delay={500}
          />
        ) : (
          <Card delay={500}>
            <CardLabel>Registration Status</CardLabel>
            <p className="text-white/40 text-sm font-body mt-6">
              Status breakdown not available.
            </p>
          </Card>
        )}
        <TopBars
          title="Top Teams by Registered Users"
          items={topTeams}
          delay={600}
          accentTop={ACCENT_GREEN}
          accentRest={ACCENT_BLUE}
        />
      </div>
    </>
  );
}

export const appUsageSlide: Slide = {
  id: "app-usage",
  title: "App Usage",
  render: (ctx) => <AppUsageSlideView {...ctx} />,
};
